/**
 * GoldenProcessTimeline — the task's canonical step sequence as duration
 * bars (bar = median turn span across analyzed rollouts), with a
 * step-detail panel: step description left, mapped failures right.
 * Selection is controlled by the page so it can highlight the matching
 * turn span in the open transcript.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  failuresByStep,
  humanizeTag,
  processEntryFor,
  KNOWN_OUTCOMES,
  outcomeFill,
  stepSpans,
  type MappedFailure,
  type StepSpan,
} from "@/components/benchmarks/goldenProcess";
import { formatScore } from "@/components/evals/format";
import { cn } from "@/lib/utils";
import type {
  ProcessRollout,
  RolloutEntry,
  TaskProcessResponse,
} from "@/api/generated/model";

const MONO_LABEL =
  "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

function StepDetail({
  process,
  index,
  failures,
  span,
  selectedRollout,
  entries,
  onOpenRollout,
}: {
  process: TaskProcessResponse;
  index: number;
  failures: MappedFailure[];
  span: StepSpan;
  selectedRollout: ProcessRollout | undefined;
  entries: RolloutEntry[];
  onOpenRollout: ((entry: RolloutEntry) => void) | undefined;
}): React.JSX.Element | null {
  const step = process.steps[index];
  if (step === undefined) return null;
  const aggregate = process.step_aggregates.find((a) => a.step_id === step.id);
  const correlation = process.correlations.find((c) => c.step_id === step.id);
  const selectedRun = selectedRollout?.step_runs.find(
    (run) => run.step_id === step.id,
  );
  return (
    <div className="grid gap-6 border-t border-border pt-4 lg:grid-cols-2">
      <div className="min-w-0 space-y-2">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={MONO_LABEL}>
            step {index + 1}/{process.steps.length}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {step.id}
          </span>
          {span.start !== null && span.end !== null && (
            <span className="font-mono text-[10px] text-muted-foreground">
              turns {span.start}-{span.end} (median)
            </span>
          )}
          {failures.length > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-brand-neutral-olive">
              divergence point
            </span>
          )}
          {aggregate !== undefined && (
            <span className="font-mono text-[10px] text-muted-foreground">
              ok rate {formatScore(aggregate.ok_rate)}
            </span>
          )}
          {correlation !== undefined && correlation.pearson_r !== null && (
            <span
              className="font-mono text-[10px] text-muted-foreground"
              title="Correlation between this step's outcome and the final reward"
            >
              r {correlation.pearson_r.toFixed(2)}
            </span>
          )}
        </p>
        <p className="text-sm leading-6 text-foreground">{step.description}</p>
        {selectedRun !== undefined && (
          <div className="rounded border border-border bg-muted/40 p-2">
            <p className={MONO_LABEL}>
              selected rollout · turns {selectedRun.turn_start}-
              {selectedRun.turn_end} · {humanizeTag(selectedRun.outcome)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedRun.what_happened}
            </p>
          </div>
        )}
      </div>
      <div className="min-w-0 space-y-3">
        <p className={MONO_LABEL}>
          {failures.length > 0
            ? `${failures.length.toString()} mapped failure${failures.length > 1 ? "s" : ""}`
            : "no mapped failures"}
        </p>
        {(() => {
          // Group by failure tag: the step-level signal is WHICH modes
          // happened here and how often; per-rollout prose is drill-in.
          const groups = new Map<string, MappedFailure[]>();
          for (const failure of failures) {
            const list = groups.get(failure.tag) ?? [];
            list.push(failure);
            groups.set(failure.tag, list);
          }
          return [...groups.entries()]
            .sort((a, b) => b[1].length - a[1].length)
            .map(([tag, group]) => {
              const byModel = new Map<string, number>();
              for (const failure of group) {
                byModel.set(
                  failure.rollout.model,
                  (byModel.get(failure.rollout.model) ?? 0) + 1,
                );
              }
              return (
                <details key={tag} className="group">
                  <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded px-1 py-0.5 hover:bg-muted/50">
                    <span className="text-sm text-foreground">
                      {humanizeTag(tag)}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      ×{group.length} ·{" "}
                      {[...byModel.entries()]
                        .map(([m, n]) => (n > 1 ? `${m} ×${n.toString()}` : m))
                        .join(" · ")}
                    </span>
                  </summary>
                  <div className="mt-1.5 space-y-3 border-l border-border pl-3">
                    {group.map((failure) => {
                      const entry = processEntryFor(failure.rollout, entries);
                      return (
                        <div key={`${failure.rollout.model}/${failure.rollout.label}`}>
                          {entry !== undefined && onOpenRollout !== undefined ? (
                            <button
                              type="button"
                              onClick={() => {
                                onOpenRollout(entry);
                              }}
                              aria-label={`Open the ${failure.rollout.model} trajectory`}
                              className="font-mono text-[10px] text-muted-foreground underline decoration-border underline-offset-2 hover:text-foreground"
                            >
                              {failure.rollout.model} · score{" "}
                              {formatScore(failure.rollout.score)} ·{" "}
                              {failure.confidence} confidence
                            </button>
                          ) : null}
                          {entry !== undefined && onOpenRollout !== undefined ? (
                            <button
                              type="button"
                              onClick={() => {
                                onOpenRollout(entry);
                              }}
                              className="ml-2 font-mono text-[10px] uppercase tracking-wider text-foreground underline underline-offset-2 hover:opacity-70"
                            >
                              view trace
                            </button>
                          ) : (
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {failure.rollout.model} · score{" "}
                              {formatScore(failure.rollout.score)} ·{" "}
                              {failure.confidence} confidence
                            </span>
                          )}
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            {failure.rootCause}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </details>
              );
            });
        })()}
      </div>
    </div>
  );
}

export function GoldenProcessTimeline({
  process,
  selectedStep,
  onSelectStep,
  selectedRollout,
  entries = [],
  onOpenRollout,
}: {
  process: TaskProcessResponse;
  selectedStep: number;
  onSelectStep: (index: number) => void;
  selectedRollout: ProcessRollout | undefined;
  entries?: RolloutEntry[];
  onOpenRollout?: (entry: RolloutEntry) => void;
}): React.JSX.Element {
  const spans = stepSpans(process);
  const failures = failuresByStep(process);
  const axisMax = Math.max(
    1,
    ...[...spans.values()].map((span) => span.end ?? 0),
  );
  const safeSelected = Math.min(selectedStep, process.steps.length - 1);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Golden process · {process.steps.length} steps
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          {process.steps.map((step, index) => {
            const on = index === safeSelected;
            const span = spans.get(step.id);
            const stepFailures = failures.get(step.id) ?? [];
            // The open rollout's outcome colors the bar, so the bar and
            // the transcript rail speak the same palette.
            const selectedRun = selectedRollout?.step_runs.find(
              (run) => run.step_id === step.id,
            );
            // The bar runs the step's median turn window; 1-based turns
            // map to a 0-based axis so step one starts at the left edge.
            const left =
              span?.start != null ? ((span.start - 1) / axisMax) * 100 : null;
            const width =
              span?.start != null && span.end != null
                ? Math.max(((span.end - span.start + 1) / axisMax) * 100, 1.5)
                : null;
            return (
              <Tooltip key={step.id} delayDuration={500}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => {
                      onSelectStep(index);
                    }}
                    aria-current={on ? "step" : undefined}
                    className={cn(
                      "grid w-full grid-cols-[minmax(140px,220px)_1fr_88px] items-center gap-3 rounded px-2 py-1.5 text-left transition-colors",
                      on ? "bg-muted" : "hover:bg-muted/50",
                    )}
                  >
                    <span
                      className={cn(
                        "truncate text-sm",
                        on ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      {step.title}
                    </span>
                    <span className="relative h-2.5">
                      <span
                        className="absolute inset-x-0 top-1 h-px bg-border"
                        aria-hidden="true"
                      />
                      {left !== null && width !== null ? (
                        <span
                          aria-hidden="true"
                          className={cn(
                            "absolute top-0 h-2 rounded",
                            selectedRun === undefined &&
                              (on ? "bg-foreground" : "bg-muted-foreground/40"),
                            selectedRun !== undefined &&
                              !on &&
                              "opacity-70",
                            selectedRun !== undefined &&
                              on &&
                              "ring-1 ring-foreground",
                          )}
                          style={{
                            left: `${left.toString()}%`,
                            width: `${width.toString()}%`,
                            ...(selectedRun !== undefined
                              ? { backgroundColor: outcomeFill(selectedRun.outcome) }
                              : {}),
                          }}
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="absolute top-0 h-2 w-4 rounded border border-dashed border-border"
                        />
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-right font-mono text-[10px]",
                        stepFailures.length > 0
                          ? "text-brand-neutral-olive"
                          : "text-muted-foreground",
                      )}
                    >
                      {stepFailures.length > 0
                        ? `${stepFailures.length.toString()} issue${stepFailures.length > 1 ? "s" : ""}`
                        : span?.start != null && span.end != null
                          ? `~${(span.end - span.start + 1).toString()} turns`
                          : "no runs"}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-96">
                  <p className="text-xs text-foreground">
                    {index + 1}. {step.title}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {step.id}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {span?.start != null && span.end != null
                      ? `turns ${span.start.toString()}-${span.end.toString()} (median)`
                      : "no runs"}
                    {stepFailures.length > 0 &&
                      ` · ${stepFailures.length.toString()} mapped failure${stepFailures.length > 1 ? "s" : ""}`}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          <div className="grid grid-cols-[minmax(140px,220px)_1fr_88px] items-center gap-3 px-2 pt-1">
            <span className="text-xs text-muted-foreground">
              Bars span each step&apos;s median turn window.
            </span>
            <span className="flex justify-between font-mono text-[10px] text-muted-foreground">
              <span>turn 1</span>
              <span>turn {Math.round(axisMax / 2)}</span>
              <span>turn {axisMax}</span>
            </span>
            <span />
          </div>
          {selectedRollout !== undefined && (
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <span className="normal-case">
                Color shows the open rollout&apos;s outcome:
              </span>
              {KNOWN_OUTCOMES.map((outcome) => (
                <span key={outcome} className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="size-2.5 rounded"
                    style={{ backgroundColor: outcomeFill(outcome) }}
                  />
                  {humanizeTag(outcome)}
                </span>
              ))}
            </p>
          )}
        </div>
        {(() => {
          const selectedStepDef = process.steps[safeSelected];
          if (selectedStepDef === undefined) return null;
          return (
            <StepDetail
              process={process}
              index={safeSelected}
              failures={failures.get(selectedStepDef.id) ?? []}
              span={spans.get(selectedStepDef.id) ?? { start: null, end: null }}
              selectedRollout={selectedRollout}
              entries={entries}
              onOpenRollout={onOpenRollout}
            />
          );
        })()}
      </CardContent>
    </Card>
  );
}
